import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { Car, Clock, Shield, Truck, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const uspItems = [
  { icon: Clock, text: 'Schnelle Bewertung' },
  { icon: Shield, text: 'Faire Preise' },
  { icon: CheckCircle, text: 'Sofortige Anfrage' },
  { icon: Car, text: 'Ankauf auch mit Mängeln' },
  { icon: Truck, text: 'Abholung möglich' },
];

const trustBadges = [
  'Faire Bewertung',
  'Schnelle Abwicklung',
  'Ankauf auch mit Schäden',
  'Deutschlandweit',
  'Europaweit',
  'Kostenlose Anfrage',
];

export const Hero = () => {
  const scrollToForm = () => {
    const element = document.querySelector('#autoformular');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToContact = () => {
    const element = document.querySelector('#kontakt');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-tiktok-black via-[#0a0a0a] to-tiktok-surface">
      {/* Subtle gradient overlays */}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-tiktok-cyan/5 to-transparent" />
      <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-tiktok-pink/5 to-transparent" />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 md:px-6 pt-24 pb-32">
        <div className="max-w-3xl">
          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-wrap gap-2 mb-6"
          >
            {trustBadges.map((badge, index) => (
              <span
                key={index}
                className="trust-badge px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-white/10 border border-white/20 rounded-full text-white"
              >
                {badge}
              </span>
            ))}
          </motion.div>

          {/* Main Headline - EINZIGE H1 auf der gesamten Seite */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white mb-4"
          >
            PKW{' '}
            <span className="text-tiktok-pink neon-text-pink">Ankauf</span>{' '}
            <span className="text-tiktok-cyan neon-text-cyan">24/7</span>
            <span className="sr-only"> - Autoankauf deutschlandweit</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-gray-300 mb-3 max-w-xl"
          >
            Wir kaufen dein Auto schnell, fair und unkompliziert.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="text-base text-gray-400 mb-8 max-w-xl"
          >
            Heute anfragen. Schnelle Rückmeldung. Auf Wunsch Abholung.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 mb-12"
          >
            <Button
              onClick={scrollToForm}
              data-testid="hero-cta-bewerten"
              className="btn-gradient text-white font-bold text-lg px-8 py-6 rounded-xl hover:scale-105 transition-transform active:scale-95"
            >
              Fahrzeug kostenlos bewerten
            </Button>
            <Button
              onClick={scrollToContact}
              data-testid="hero-cta-kontakt"
              variant="outline"
              className="border-2 border-white/30 text-white bg-transparent hover:bg-white/10 font-semibold text-lg px-8 py-6 rounded-xl"
            >
              Jetzt Kontakt aufnehmen
            </Button>
          </motion.div>
        </div>
      </div>

      {/* USP Marquee */}
      <div className="absolute bottom-0 left-0 right-0 bg-tiktok-surface/90 backdrop-blur-sm border-t border-white/10 py-4">
        <Marquee gradient={false} speed={40}>
          {uspItems.map((item, index) => (
            <div key={index} className="marquee-item">
              <item.icon className="w-4 h-4 text-tiktok-cyan" />
              <span>{item.text}</span>
            </div>
          ))}
          {uspItems.map((item, index) => (
            <div key={`repeat-${index}`} className="marquee-item">
              <item.icon className="w-4 h-4 text-tiktok-cyan" />
              <span>{item.text}</span>
            </div>
          ))}
        </Marquee>
      </div>
    </section>
  );
};

export default Hero;
